import React, { useState, useEffect, useRef, useContext } from 'react';
import './myJoyNetFeed.css';
import { ReactComponent as ShareIcon } from '../../assets/icons/share.svg';
import { ReactComponent as FlagIcon } from '../../assets/icons/flag.svg';
import { ReactComponent as CommentIcon } from '../../assets/icons/comment.svg';
import { ReactComponent as LikeIcon } from '../../assets/icons/like.svg';
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg';
import { db } from "../../firebase";
import { collection, query, orderBy, limit, getDocs, startAfter, doc, getDoc, updateDoc, increment, setDoc, addDoc, serverTimestamp } from "firebase/firestore";
import ShareModal from '../ShareModal/ShareModal';
import { UserContext } from '../../context/UserContext';
import SendIcon from '../../assets/icons/CTA.svg';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import 'react-quill/dist/quill.snow.css';

// Helper to check if a string looks like a URL
const looksLikeUrl = (str) => typeof str === 'string' && str.match(/^https?:\/\//i);

//Helper to extract the video ID from the Tiktok url
const extractTikTokId = (url) => {
  const regex = /(?:\/video\/|\/v\/)(\d{10,20})/;
  const match = url.match(regex);
  return match ? match[1] : null;
}

// Helper to check if a post has a direct image or video URL
const hasDirectMedia = (post) => looksLikeUrl(post.url);

const PostMedia = ({ post, onPlay, isVisible, isScrolling, autoplayEnabled }) => {
    // Hooks must be at the top
    const [imgError, setImgError] = React.useState(false);
    const [videoError, setVideoError] = React.useState(false);
    const videoRef = React.useRef(null);
    const iframeRef = React.useRef(null);

    // TikTok embed messaging functions
    const sendTikTokMessage = (type, value = undefined) => {
        if (iframeRef.current) {
            const message = {
                'x-tiktok-player': true,
                type: type,
                value: value
            };
            try {
                iframeRef.current.contentWindow.postMessage(message, '*');
            } catch (e) {
                console.log('TikTok message failed:', e);
            }
        }
    };

    // Auto play/pause for regular videos based on visibility
    React.useEffect(() => {
        if (videoRef.current && !isScrolling && autoplayEnabled) {
            if (isVisible) {
                console.log(`Auto-playing video: ${post.id}`);
                videoRef.current.play().catch(e => console.log('Auto play failed:', e));
            } else {
                console.log(`Auto-pausing video: ${post.id}`);
                videoRef.current.pause();
            }
        }
    }, [isVisible, isScrolling, autoplayEnabled]);

    // Auto play/pause for TikTok iframes using official API
    React.useEffect(() => {
        if (post.source === 'tiktok' && iframeRef.current && autoplayEnabled && !isScrolling) {
            // Add a small delay to ensure TikTok player is ready
            const timeoutId = setTimeout(() => {
                if (isVisible) {
                    console.log(`Sending play message to TikTok video: ${post.id}`);
                    sendTikTokMessage('play');
                } else {
                    console.log(`Sending pause message to TikTok video: ${post.id}`);
                    sendTikTokMessage('pause');
                }
            }, 500); // Give TikTok player time to initialize

            return () => clearTimeout(timeoutId);
        }
    }, [isVisible, isScrolling, autoplayEnabled, post.source]);

    // Listen for TikTok player messages
    React.useEffect(() => {
        const handleTikTokMessage = (event) => {
            if (event.data && event.data['x-tiktok-player']) {
                const { type, value } = event.data;
                switch (type) {
                    case 'onPlayerReady':
                        console.log('TikTok player ready');
                        break;
                    case 'onStateChange':
                        // -1: init, 0: ended, 1: playing, 2: paused, 3: buffering
                        if (value === 1 && onPlay) {
                            onPlay();
                        }
                        break;
                    case 'onCurrentTime':
                        // Handle current time updates if needed
                        break;
                    case 'onError':
                        console.error('TikTok player error:', value);
                        break;
                }
            }
        };

        window.addEventListener('message', handleTikTokMessage);
        return () => window.removeEventListener('message', handleTikTokMessage);
    }, [onPlay]);

    // Gather all possible fields that could contain a media URL
    const possibleFields = [
        'url', 'image_url', 'video_url', 'media_url', 'thumbnail_url',
        'photo', 'picture', 'media', 'src', 'link', 'file', 'file_url', 'contentUrl', 'content_url'
    ];
    const allUrls = possibleFields
        .map(field => post[field])
        .filter(looksLikeUrl);

    // TikTok embed: Only render if we have a direct video URL
    if (post.source === 'tiktok' && post.id) {
        const tiktokMediaUrl = post.url;
        const v_id = extractTikTokId(tiktokMediaUrl);
        if (v_id) {
            return (
                <div className="video-container">
                    <iframe
                        ref={iframeRef}
                        src={`https://www.tiktok.com/player/v1/${v_id}?&music_info=1&description=1`}
                        allowFullScreen
                        allow="autoplay; fullscreen"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: 'none',
                            margin: 0,
                            padding: 0,
                            background: 'transparent'
                        }}
                        title={`TikTok video ${v_id}`}
                    />
                </div>
            );
        }
        // If not a direct video/image, do not render the embed
        return null;
    }
    // Instagram embed: Only render if we have a direct video or image URL
    if (post.source === 'instagram' && post.code) {
        const shortcode = post.code;
        return (
            <div className="video-container">
                <iframe
                    ref={iframeRef}
                    src={`https://www.instagram.com/p/${shortcode}/embed?hidecaption=true`}
                    allowTransparency={true}
                    allowFullScreen={true}
                    frameBorder="0"
                    scrolling="no"
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        border: 'none',
                        margin: 0,
                        padding: 0,
                        background: 'transparent'
                    }}
                    title={`Instagram Post ${shortcode}`}
                    onPlay={onPlay}
                />
            </div>
    );
    }
    // Threads embed (keep as is, or add similar logic if needed)
    if (post.source === 'threads' && post.id) {
        return (
            <video
                ref={videoRef}
                src={post.url}
                controls
                muted
                playsInline
                style={{ maxWidth: '100%', borderRadius: 8, background: '#000' }}
                onPlay={onPlay}
                onError={() => setVideoError(true)}
            />
        );
    }

    // Try to render any found URL as image, then as video if image fails
    for (let i = 0; i < allUrls.length; i++) {
        const url = allUrls[i];
        if (!imgError) {
            return (
                <img
                    src={url}
                    alt={post.description || post.text || 'post media'}
                    style={{ maxWidth: '100%', borderRadius: 8, objectFit: 'contain' }}
                    onLoad={onPlay}
                    onError={() => setImgError(true)}
                />
            );
        }
        if (imgError && !videoError) {
            return (
                <video
                    ref={videoRef}
                    src={url}
                    controls
                    muted
                    playsInline
                    style={{ maxWidth: '100%', borderRadius: 8, background: '#000' }}
                    onPlay={onPlay}
                    onError={() => setVideoError(true)}
                />
            );
        }
    }
    // No media to show or all failed
    return null;
};

const MyJoynetFeed = ({ initialPostId }) => {
    const inputRef = useRef(null);
    const [showFormattingMenu, setShowFormattingMenu] = useState(false);
    const { user } = useContext(UserContext);
    const [posts, setPosts] = useState([]);
    const [lastDoc, setLastDoc] = useState({
        instagram: null,
        threads: null,
        tiktok: null
    });
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [selectedPost, setSelectedPost] = useState(null);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [videoStats, setVideoStats] = useState({});
    const [likedVideos, setLikedVideos] = useState(new Set());
    const observerRef = useRef();
    const emojiPickerRef = useRef(null);
    const containerRef = useRef();
    const targetPostRef = useRef();
    const formattingMenuRef = useRef(null);
    const videoObserverRef = useRef(null);
    const [newComment, setNewComment] = useState("");
    const [showComments, setShowComments] = useState(false);
    const [comments, setComments] = useState([]);
    const [activePostId, setActivePostId] = useState(null);
    const [visibleVideos, setVisibleVideos] = useState(new Set());
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef(null);
    const [autoplayEnabled, setAutoplayEnabled] = useState(() => {
        const saved = localStorage.getItem('joynet-autoplay-enabled');
        return saved !== null ? JSON.parse(saved) : true;
    });

    const [showEmojiPicker, setShowEmojiPicker] = useState(false);

    const [error, setError] = useState(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    const submitComment = async (postId) => {
        if (!newComment.trim() || !user) return;

        try {
            const commentData = {
                user_id: user.uid,
                username: user.displayName || "Anonymous",
                text: newComment.trim(),
                timestamp: serverTimestamp()
            };

            const commentsRef = collection(db, "comments", postId, "videoComments");
            await addDoc(commentsRef, commentData);
            setNewComment("");
            await loadComments(postId); // refresh comment list
        } catch (error) {
            console.error("Error posting comment:", error);
        }
    };

    const toggleComments = async (postId) => {
        if (postId === activePostId && showComments) {
            setShowComments(false);
            setActivePostId(null);
        } else {
            setShowComments(true);
            setActivePostId(postId);
            await loadComments(postId);
        }
    };

    const loadComments = async (postId) => {
        try {
            const commentsRef = collection(db, "comments", postId, "videoComments");
            const q = query(commentsRef, orderBy("timestamp", "desc"));
            const snapshot = await getDocs(q);
            const loadedComments = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setComments(loadedComments);
        } catch (error) {
            console.error("Error loading comments:", error);
        }
    };

    useEffect(() => {
        if (user) {
            fetchLikedVideos();
        }
    }, [user]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (emojiPickerRef.current && !emojiPickerRef.current.contains(event.target)) {
                setShowEmojiPicker(false);
            }
            if (formattingMenuRef.current && !formattingMenuRef.current.contains(event.target)) {
                setShowFormattingMenu(false);
            }
        };

        if (showEmojiPicker || showFormattingMenu) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Save autoplay preference to localStorage
    useEffect(() => {
        localStorage.setItem('joynet-autoplay-enabled', JSON.stringify(autoplayEnabled));
    }, [autoplayEnabled]);

    const applyFormatting = (format) => {
        const input = inputRef.current;
        if (!input) return;

        const start = input.selectionStart;
        const end = input.selectionEnd;
        const selectedText = newComment.substring(start, end);
        let newText = '';

        switch (format) {
            case 'bold':
                newText = `<b>${selectedText}</b>`;
                break;
            case 'italic':
                newText = `<i>${selectedText}</i>`;
                break;
            case 'underline':
                newText = `<u>${selectedText}</u>`;
                break;
            case 'strikethrough':
                newText = `<s>${selectedText}</s>`;
                break;
            default:
                return;
        }

        const newContent = newComment.substring(0, start) + newText + newComment.substring(end);
        setNewComment(newContent);
        setShowFormattingMenu(false);
    };

    const toggleFormattingMenu = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowFormattingMenu(prev => !prev);
    };

    const toggleEmojiPicker = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowEmojiPicker(prev => !prev);
    };

    const fetchLikedVideos = async () => {
        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const userDoc = await getDoc(userRef);
            if (userDoc.exists() && userDoc.data().likedVideos) {
                setLikedVideos(new Set(userDoc.data().likedVideos));
            }
        } catch (error) {
            console.error('Error fetching liked videos:', error);
        }
    };

    const handleEmojiSelect = (emoji) => {
        const input = inputRef.current;
        if (input) {
            const start = input.selectionStart;
            const end = input.selectionEnd;
            const newValue = newComment.substring(0, start) + emoji.native + newComment.substring(end);
            setNewComment(newValue);
            
            // Set cursor position after emoji
            setTimeout(() => {
                input.selectionStart = input.selectionEnd = start + emoji.native.length;
                input.focus();
            }, 0);
        }
    };

    const updateVideoStats = async (v_id, field, isIncrement = true) => {
        try {
            const statsRef = doc(db, "video_stats", v_id);
            const statsDoc = await getDoc(statsRef);
            
            if (statsDoc.exists()) {
                await updateDoc(statsRef, {
                    [field]: increment(isIncrement ? 1 : -1)
                });
            } else {
                await setDoc(statsRef, {
                    v_id: v_id,
                    total_shares: field === 'total_shares' ? 1 : 0,
                    total_likes: field === 'total_likes' ? (isIncrement ? 1 : 0) : 0,
                    total_plays: field === 'total_plays' ? 1 : 0
                });
            }
        } catch (error) {
            console.error('Error updating video stats:', error);
            // Revert optimistic update on error
            setVideoStats(prev => {
                const newStats = { ...prev };
                newStats[v_id][field] = (newStats[v_id][field] || 0) + (isIncrement ? -1 : 1); // Revert the change
                return newStats;
            });
        }
    };

    const handleLike = async (post) => {
        if (!user) return;

        const postId = post.id;
        const wasLiked = likedVideos.has(postId);

        try {
            const userRef = doc(db, "UserInformation", user.uid);
            const statsRef = doc(db, "video_stats", postId);
            const statsDoc = await getDoc(statsRef);

            // Only update the liked state in UI
            setLikedVideos(prev => {
                const newSet = new Set(prev);
                if (wasLiked) {
                    newSet.delete(postId);
                } else {
                    newSet.add(postId);
                }
                return newSet;
            });

            // Update backend
            await Promise.all([
                updateDoc(userRef, {
                    likedVideos: Array.from(wasLiked ? 
                        new Set([...likedVideos].filter(id => id !== postId)) : 
                        new Set([...likedVideos, postId]))
                }),
                statsDoc.exists() 
                    ? updateDoc(statsRef, { total_likes: increment(wasLiked ? -1 : 1) })
                    : setDoc(statsRef, {
                        post_id: postId,
                        source: post.source,
                        total_shares: 0,
                        total_likes: wasLiked ? 0 : 1,
                        total_plays: 0,
                        total_comments: 0
                    })
            ]);

            // Get the updated stats after backend update
            const updatedStatsDoc = await getDoc(statsRef);
            if (updatedStatsDoc.exists()) {
                setVideoStats(prev => ({
                    ...prev,
                    [postId]: updatedStatsDoc.data()
                }));
            }
        } catch (error) {
            console.error('Error updating like status:', error);
            // Revert UI updates on error
            setLikedVideos(prev => {
                const newSet = new Set(prev);
                if (wasLiked) {
                    newSet.add(postId);
                } else {
                    newSet.delete(postId);
                }
                return newSet;
            });
        }
    };

    const handlePlay = async (post) => {
        // Optimistically update UI
        setVideoStats(prev => {
            const newStats = { ...prev };
            if (!newStats[post.id]) {
                newStats[post.id] = { total_likes: 0, total_shares: 0, total_plays: 0 };
            }
            newStats[post.id].total_plays = (newStats[post.id].total_plays || 0) + 1;
            return newStats;
        });

        await updateVideoStats(post.id, 'total_plays');
    };

    const fetchInitialPosts = async () => {
        setIsInitialLoading(true);
        setError(null);
        try {
            let initialPost = null;
            if (initialPostId) {
                // Try to fetch the post from all three sources by documentId
                const sources = [
                    { collection: 'scraped_data/instagram/posts', source: 'instagram' },
                    { collection: 'scraped_data/threads/posts', source: 'threads' },
                    { collection: 'scraped_data/tiktok/posts', source: 'tiktok' }
                ];
                for (const src of sources) {
                    const postRef = doc(db, src.collection, initialPostId);
                    const postDoc = await getDoc(postRef);
                    if (postDoc.exists()) {
                        initialPost = { id: postDoc.id, source: src.source, ...postDoc.data() };
                        break;
                    }
                }
            }

            // Fetch initial posts from each source
            const [instagramSnapshot, threadsSnapshot, tiktokSnapshot] = await Promise.all([
                getDocs(query(
                    collection(db, "scraped_data/instagram/posts"),
                    limit(10)
                )),
                getDocs(query(
                    collection(db, "scraped_data/threads/posts"),
                    limit(10)
                )),
                getDocs(query(
                    collection(db, "scraped_data/tiktok/posts"),
                    limit(10)
                ))
            ]);

            // Store last documents for pagination
            const lastDocs = {
                instagram: instagramSnapshot.docs.length > 0 ? instagramSnapshot.docs[instagramSnapshot.docs.length - 1] : null,
                threads: threadsSnapshot.docs.length > 0 ? threadsSnapshot.docs[threadsSnapshot.docs.length - 1] : null,
                tiktok: tiktokSnapshot.docs.length > 0 ? tiktokSnapshot.docs[tiktokSnapshot.docs.length - 1] : null,
            };

            console.log(lastDocs);
            setLastDoc(lastDocs);

            // Combine all posts
            let allPosts = [
                ...instagramSnapshot.docs.map(doc => ({ id: doc.id, source: 'instagram', ...doc.data() })),
                ...threadsSnapshot.docs.map(doc => ({ id: doc.id, source: 'threads', ...doc.data() })),
                ...tiktokSnapshot.docs.map(doc => ({ id: doc.id, source: 'tiktok', ...doc.data() }))
            ];

            // Shuffle the posts using Fisher-Yates algorithm for better randomization
            for (let i = allPosts.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [allPosts[i], allPosts[j]] = [allPosts[j], allPosts[i]];
            }

            // If initialPost exists, put it at the top
            if (initialPost) {
                allPosts = [initialPost, ...allPosts];
            }
            
            setPosts(allPosts);

            // Initialize or fetch stats for all posts
            const statsPromises = allPosts.map(async (post) => {
                const postId = post.id;
                const statsRef = doc(db, "video_stats", postId);
                const statsDoc = await getDoc(statsRef);

                if (!statsDoc.exists()) {
                    const initialStats = {
                        post_id: postId,
                        source: post.source,
                        total_shares: 0,
                        total_likes: 0,
                        total_plays: 0,
                        total_comments: 0
                    };
                    await setDoc(statsRef, initialStats);
                    return { postId, stats: initialStats };
                }

                return { postId, stats: statsDoc.data() };
            });

            const statsResults = await Promise.all(statsPromises);
            const newStats = {};
            statsResults.forEach(result => {
                newStats[result.postId] = result.stats;
            });
            setVideoStats(newStats);

            // Check if there are more posts to load
            const hasMorePosts = instagramSnapshot.docs.length === 10 || 
                               threadsSnapshot.docs.length === 10 || 
                               tiktokSnapshot.docs.length === 10;
            setHasMore(hasMorePosts);
        } catch (error) {
            console.error("Error fetching posts:", error);
            setError("Failed to load posts. Please try again.");
        } finally {
            setIsInitialLoading(false);
            setLoading(false);
        }
    };

    const fetchMorePosts = async () => {
        console.log("trying");
        console.log({ loading, hasMore, lastDoc });
        if (loading || !hasMore || !lastDoc) return;

        setLoading(true);
        setError(null);

        try {
            const fetchPromises = [];
            const sources = [];

            if (lastDoc.instagram) {
                fetchPromises.push(getDocs(query(
                    collection(db, "scraped_data/instagram/posts"),
                    startAfter(lastDoc.instagram),
                    limit(10)
                )));
                sources.push("instagram");
            }

            if (lastDoc.threads) {
                fetchPromises.push(getDocs(query(
                    collection(db, "scraped_data/threads/posts"),
                    startAfter(lastDoc.threads),
                    limit(10)
                )));
                sources.push("threads");
            }

            if (lastDoc.tiktok) {
                fetchPromises.push(getDocs(query(
                    collection(db, "scraped_data/tiktok/posts"),
                    startAfter(lastDoc.tiktok),
                    limit(10)
                )));
                sources.push("tiktok");
            }

            const snapshots = await Promise.all(fetchPromises);

            const newLastDocs = { ...lastDoc };
            const newPosts = [];

            snapshots.forEach((snap, index) => {
                const source = sources[index];
                if (snap.docs.length > 0) {
                    newLastDocs[source] = snap.docs[snap.docs.length - 1];
                } else {
                    newLastDocs[source] = null;
                }

                newPosts.push(...snap.docs.map(doc => ({
                    id: doc.id,
                    source,
                    ...doc.data()
                })));
            });

            setLastDoc(newLastDocs);

            // Shuffle
            for (let i = newPosts.length - 1; i > 0; i--) {
                const j = Math.floor(Math.random() * (i + 1));
                [newPosts[i], newPosts[j]] = [newPosts[j], newPosts[i]];
            }

            setPosts(prev => [...prev, ...newPosts]);

            // Stats
            const statsPromises = newPosts.map(async (post) => {
                const postId = post.id;
                const statsRef = doc(db, "video_stats", postId);
                const statsDoc = await getDoc(statsRef);

                if (!statsDoc.exists()) {
                    const initialStats = {
                        post_id: postId,
                        source: post.source,
                        total_shares: 0,
                        total_likes: 0,
                        total_plays: 0,
                        total_comments: 0
                    };
                    await setDoc(statsRef, initialStats);
                    return { postId, stats: initialStats };
                }

                return { postId, stats: statsDoc.data() };
            });

            const statsResults = await Promise.all(statsPromises);
            const newStats = { ...videoStats };
            statsResults.forEach(result => {
                newStats[result.postId] = result.stats;
            });
            setVideoStats(newStats);

            const hasMorePosts = snapshots.some(snap => snap.docs.length === 10);
            setHasMore(hasMorePosts);

            console.log("Added more!");
            
            // Trigger video observer setup for new posts
            setTimeout(() => {
                setupVideoObserver();
            }, 300);
        } catch (error) {
            console.error("Error fetching more posts:", error);
            setError("Failed to load more posts. Please try again.");
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchInitialPosts();
    }, [initialPostId]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                console.log('Observer triggered', entries[0]);
                const target = entries[0];
                if (target.isIntersecting) {
                    fetchMorePosts();
                }
            },
            { 
                threshold: 0.1,
                rootMargin: '100px' // Start loading before reaching the bottom
            }
        );

        if (observerRef.current) {
            observer.observe(observerRef.current);
        }

        return () => {
            if (observerRef.current) {
                observer.unobserve(observerRef.current);
            }
        };
    }, [hasMore, loading]);

    useEffect(() => {
        const loadTikTokScript = () => {
            const existingScript = document.getElementById('tiktok-embed-script');
            if (!existingScript) {
                const script = document.createElement('script');
                script.id = 'tiktok-embed-script';
                script.src = 'https://www.tiktok.com/embed.js';
                script.async = true;
                document.body.appendChild(script);
            }
        };

        loadTikTokScript();
    }, [posts]);

    useEffect(() => {
        if (initialPostId && targetPostRef.current) {
            targetPostRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [initialPostId, posts]);

    // Save autoplay preference to localStorage
    useEffect(() => {
        localStorage.setItem('joynet-autoplay-enabled', JSON.stringify(autoplayEnabled));
    }, [autoplayEnabled]);

    // Function to create and setup video observer
    const setupVideoObserver = () => {
        // Cleanup existing observer
        if (videoObserverRef.current) {
            videoObserverRef.current.disconnect();
        }

        videoObserverRef.current = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    const postId = entry.target.getAttribute('data-post-id');
                    if (postId) {
                        setVisibleVideos(prev => {
                            const newSet = new Set(prev);
                            if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
                                newSet.add(postId);
                            } else {
                                newSet.delete(postId);
                            }
                            return newSet;
                        });
                    }
                });
            },
            {
                threshold: 0.5, // Video needs to be 50% visible to autoplay
                rootMargin: '0px'
            }
        );

        // Observe all video containers
        const videoContainers = document.querySelectorAll('.post-media[data-post-id]');
        console.log(`Setting up observer for ${videoContainers.length} video containers`);
        videoContainers.forEach(container => {
            videoObserverRef.current.observe(container);
        });
    };

    // Video visibility observer for autoplay/autopause
    useEffect(() => {
        // Use a timeout to ensure DOM is updated after posts render
        const timeoutId = setTimeout(() => {
            setupVideoObserver();
        }, 100);

        return () => {
            clearTimeout(timeoutId);
            if (videoObserverRef.current) {
                videoObserverRef.current.disconnect();
            }
        };
    }, [posts]); // Re-run when posts change

    // Scroll detection to pause videos during fast scrolling
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolling(true);
            
            // Clear existing timeout
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
            
            // Set timeout to detect when scrolling stops
            scrollTimeoutRef.current = setTimeout(() => {
                setIsScrolling(false);
            }, 150); // 150ms after scrolling stops
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    // Additional observer to catch DOM changes for newly added posts
    useEffect(() => {
        const postsContainer = document.querySelector('.posts-container');
        if (!postsContainer) return;

        const mutationObserver = new MutationObserver((mutations) => {
            let hasNewPosts = false;
            mutations.forEach((mutation) => {
                if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
                    mutation.addedNodes.forEach((node) => {
                        if (node.nodeType === Node.ELEMENT_NODE && node.classList?.contains('post-card')) {
                            hasNewPosts = true;
                        }
                    });
                }
            });

            if (hasNewPosts) {
                console.log('Mutation observer detected new posts, re-setting up video observer');
                // Re-setup video observation for new posts
                setTimeout(() => {
                    setupVideoObserver();
                }, 200);
            }
        });

        mutationObserver.observe(postsContainer, {
            childList: true,
            subtree: true
        });

        return () => {
            mutationObserver.disconnect();
        };
    }, []);

    const createPopup = (messageText, type = 'success') => {
        const popup = document.createElement('div');
        popup.className = `popup ${type}`;
        
        const closeBtn = document.createElement('button');
        closeBtn.className = 'popup-close';
        closeBtn.innerHTML = '×';
        closeBtn.onclick = () => popup.remove();
        
        const message = document.createElement('div');
        message.className = 'popup-message';
        message.innerHTML = messageText;
        
        popup.appendChild(closeBtn);
        popup.appendChild(message);
        document.body.appendChild(popup);

        setTimeout(() => {
            popup.remove();
        }, 2000);
    };

    const handleReportContent = async (post) => {
        if (!user) {
            createPopup("You must be logged in to report content.", "error");
            return;
        }

        const confirmed = window.confirm("Are you sure you want to report this content?");
        if (!confirmed) return;

        try {
            await addDoc(collection(db, 'content_reported'), {
            postId: post.id,
            reportedBy: user.uid,
            timestamp: serverTimestamp(),
            platform: post.source
            });
            createPopup("Content reported. Thank you for helping keep the platform safe.", "success");
        } catch (error) {
            console.error("Error reporting content:", error);
            createPopup("Failed to report content. Please try again.", "error");
        }
    };

    const handleShareClick = (post) => {
        console.log("Share clicked for post:", post); // Debug log
        setSelectedPost(post);
        setIsShareModalOpen(true);
    };

    const handleCloseShareModal = () => {
        setIsShareModalOpen(false);
        setSelectedPost(null);
    };

    const handleShare = async (post) => {
        const postId = post.id;
        try {
            const statsRef = doc(db, "video_stats", postId);
            
            // Update UI immediately
            setVideoStats(prev => {
                const newStats = { ...prev };
                if (!newStats[postId]) {
                    newStats[postId] = { total_likes: 0, total_shares: 0, total_plays: 0, total_comments: 0 };
                }
                newStats[postId].total_shares = (newStats[postId].total_shares || 0) + 1;
                return newStats;
            });

            // Update backend
            const statsDoc = await getDoc(statsRef);
            if (statsDoc.exists()) {
                await updateDoc(statsRef, { total_shares: increment(1) });
            } else {
                await setDoc(statsRef, {
                    post_id: postId,
                    source: post.source,
                    total_shares: 1,
                    total_likes: 0,
                    total_plays: 0,
                    total_comments: 0
                });
            }

            // Close the share modal after successful share
            handleCloseShareModal();
        } catch (error) {
            console.error('Error updating share count:', error);
            // Revert UI update on error
            setVideoStats(prev => {
                const newStats = { ...prev };
                if (!newStats[postId]) return prev;
                newStats[postId].total_shares = Math.max(0, (newStats[postId].total_shares || 0) - 1);
                return newStats;
            });
        }
    };

    // Expanding content logic
    const [expandedPosts, setExpandedPosts] = useState(new Set());
    const toggleExpand = (postId) => {
        setExpandedPosts(prev => {
            const next = new Set(prev);
            if (next.has(postId)) {
            next.delete(postId);
            } else {
            next.add(postId);
            }
            return next;
        });
    };

    return (
        <>
            <div className="feed-container" ref={containerRef}>
                <div className="feed-main">
                    <div className="feed-header">
                        <div className="feed-title-section">
                            <h1 className="feed-title">Feeds</h1>
                            <p className="feed-subtitle">
                                Your personal timeline, showcasing a vibrant feed of videos and pictures
                                shared by friends and those you follow.
                            </p>
                        </div>
                        <div className="feed-controls">
                            <button 
                                className={`autoplay-toggle ${autoplayEnabled ? 'enabled' : 'disabled'}`}
                                onClick={() => setAutoplayEnabled(!autoplayEnabled)}
                                title={`${autoplayEnabled ? 'Disable' : 'Enable'} autoplay`}
                            >
                                {autoplayEnabled ? '⏸️' : '▶️'} Autoplay
                            </button>
                        </div>
                    </div>

                    {isInitialLoading ? (
                        <div className="loading-container">
                            <div className="loading-spinner">Loading initial posts...</div>
                        </div>
                    ) : error ? (
                        <div className="error-message">{error}</div>
                    ) : (
                        <div className="posts-container">
                            {posts.map(post => {
                                const postId = post.id;
                                // Skip Instagram/TikTok posts without direct media
                                if (!hasDirectMedia(post)) {
                                    return null;
                                }
                                
                                const isExpanded = expandedPosts.has(post.id);
                                const content = post.description || post.caption || post.text;

                                return (
                                    <div 
                                        key={post.id} 
                                        className="post-card"
                                        ref={initialPostId === postId ? targetPostRef : null}
                                    >
                                        <div className="post-header">
                                            <span className="author-username">@{post.username}</span>
                                            <span className="post-source">{post.source}</span>
                                        </div>
                                        <p className={`post-content ${!isExpanded ? 'truncated' : ''}`}>
                                            {content}
                                        </p>
                                        {content?.length > 100 && (
                                            <button 
                                                className="read-more-btn"
                                                onClick={() => toggleExpand(post.id)}
                                            >
                                                {isExpanded ? 'Show less' : 'Read more'}
                                            </button>
                                        )}
                                        <div className="post-media" data-post-id={postId}>
                                            <PostMedia 
                                                post={post} 
                                                onPlay={() => handlePlay(post)} 
                                                isVisible={visibleVideos.has(postId)}
                                                isScrolling={isScrolling}
                                                autoplayEnabled={autoplayEnabled}
                                            />
                                        </div>
                                        <div className="post-stats">
                                            <div 
                                                className="stat-item" 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleShareClick(post);
                                                }}
                                            >
                                                <ShareIcon />
                                                <span>{videoStats[postId]?.total_shares || 0}</span>
                                            </div>
                                            <div 
                                                className="stat-item" 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleLike(post);
                                                }}
                                            >
                                                <LikeIcon className={likedVideos.has(postId) ? 'liked' : ''} />
                                                <span>{videoStats[postId]?.total_likes || 0}</span>
                                            </div>
                                            <div className="stat-item">
                                                <PlayIcon />
                                                <span>{videoStats[postId]?.total_plays || 0}</span>
                                            </div>
                                            <div 
                                                className="stat-item" 
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    toggleComments(postId);
                                                }}
                                            >
                                                <CommentIcon />
                                                <span>{videoStats[postId]?.total_comments || 0}</span>
                                            </div>
                                            
                                            <div 
                                                className="stat-item"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleReportContent(post);
                                                }}
                                            >
                                                <FlagIcon />
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
            
                    <div ref={observerRef} className="loading-trigger">
                        {loading && (
                            <div className="loading-spinner">
                                Loading more posts...
                            </div>
                        )}
                        {!hasMore && posts.length > 0 && (
                            <div className="end-of-feed">
                                No more posts to load
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <div className={`comments-sidebar ${showComments ? 'visible' : ''}`}>
                <div className="comments-header">
                    <h3 className="comments-title">Comments</h3>
                    <button className="close-comments-btn" onClick={() => setShowComments(false)} aria-label="Close comments">×</button>
                </div>
                <div className="comments-list">
                    {comments.length === 0 ? (
                        <p className="no-comments">No comments yet.</p>
                    ) : (
                        comments.map(comment => (
                            <div key={comment.id} className="comment-card">
                                <div className="comment-avatar">
                                    {/* Optionally show avatar if available, else fallback */}
                                    <div className="avatar-circle">
                                        {comment.username ? comment.username.charAt(0).toUpperCase() : '?'}
                                    </div>
                                </div>
                                <div className="comment-content">
                                    <div className="comment-meta">
                                        <span className="comment-username">{comment.username}</span>
                                        {/* Optionally show timestamp if available */}
                                        {comment.timestamp && (
                                            <span className="comment-timestamp">{comment.timestamp.toDate ? comment.timestamp.toDate().toLocaleString() : ''}</span>
                                        )}
                                    </div>
                                    <div className="comment-text">{comment.text}</div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
                <form className="comment-input-form" onSubmit={(e) => {
                    e.preventDefault();
                    submitComment(activePostId);
                }}>
                    <div className="comment-input-container">
                        <button 
                            type="button"
                            className="format-button" 
                            onClick={toggleFormattingMenu}
                            aria-label="Formatting options"
                        >
                            ⋮
                        </button>
                        <input
                            ref={inputRef}
                            type="text"
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment..."
                            className="comment-input-box"
                        />
                        <button 
                            type="button"
                            className="emoji-button" 
                            onClick={toggleEmojiPicker}
                            aria-label="Emoji picker"
                        >
                            😊
                        </button>
                        <button 
                            type="submit"
                            className="send-button" 
                            disabled={!newComment.trim()}
                            aria-label="Send comment"
                        >
                            <img src={SendIcon} alt="Send" />
                        </button>
                        {showFormattingMenu && (
                            <div className="formatting-menu" ref={formattingMenuRef}>
                                <button onClick={() => applyFormatting('bold')} className="format-option"><b>B</b></button>
                                <button onClick={() => applyFormatting('italic')} className="format-option"><i>I</i></button>
                                <button onClick={() => applyFormatting('underline')} className="format-option"><u>U</u></button>
                                <button onClick={() => applyFormatting('strikethrough')} className="format-option"><s>S</s></button>
                            </div>
                        )}
                        {showEmojiPicker && (
                            <div className="emoji-picker-container" ref={emojiPickerRef}>
                                <Picker
                                    data={data}
                                    onEmojiSelect={handleEmojiSelect}
                                    theme="light"
                                    autoFocus={true}
                                    emojiSize={20}
                                    emojiButtonSize={28}
                                    perLine={8}
                                />
                            </div>
                        )}
                    </div>
                </form>
            </div>
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={handleCloseShareModal}
                post={selectedPost}
                onShare={() => {
                    console.log("Share button clicked in modal"); // Debug log
                    if (selectedPost) {
                        handleShare(selectedPost);
                    }
                }}
            />
        </>
    );
};

export default MyJoynetFeed;
